export const baselineRepository = () => {
  // Composables
  const {
    areaId,
    hasIntercept,
    hasInteractions,
    aggregationLevel,
    variables,
    baseline,
    baselineLoading,
    baselineData,
    baselines,
    loadingBaselinesList,
    baselineDataLoading,
    baselineDataError,
    baselinePrediction,
    baselinePredictionLoading,
    baselinePredictionError,
  } = storeToRefs(baselineStore());
  const { cancelRequestIfDoubled } = useCoreApiHelper();
  const { client } = useApiV2();

  const getBaselines = async () => {
    await cancelRequestIfDoubled({
      requestId: 'get-baselines',
      execute: async (signal) =>
        client.GET('/v2/baselines', {
          signal: signal,
        }),
      onSuccess: (data) => {
        baselines.value = data;
      },
      onLoadingStart: () => {
        loadingBaselinesList.value = true;
      },
      onLoadingEnd: () => {
        loadingBaselinesList.value = false;
      },
    });
  };

  const getBaseline = async (baselineId: number) => {
    await cancelRequestIfDoubled({
      requestId: 'get-baseline',
      execute: async (signal) =>
        client.GET('/v2/baselines/{baseline_id}', {
          params: { path: { baseline_id: baselineId } },
          signal,
        }),
      onSuccess: (data) => {
        baseline.value = data;
        areaId.value = data.area_id;
        hasIntercept.value = data.model.has_intercept;
        hasInteractions.value = data.model.has_interactions;
        aggregationLevel.value = data.model.aggregation_level;
        variables.value = data.model.variables;
      },
      onLoadingStart: () => {
        baselineLoading.value = true;
      },
      onLoadingEnd: () => {
        baselineLoading.value = false;
      },
    });
  };

  const createBaseline = async () => {
    if (areaId.value === undefined) return;
    const areaIdValue = areaId.value;
    const hasInterceptValue = hasIntercept.value ?? false;
    const hasInteractionsValue = hasInteractions.value ?? false;
    const aggregationLevelValue = aggregationLevel.value ?? 'day';
    if (!['day', 'week', 'month'].includes(aggregationLevelValue)) {
      throw new Error('Invalid aggregation level');
    }
    const variablesValue = variables.value ?? [];

    await cancelRequestIfDoubled({
      requestId: 'create-baseline',
      execute: async (signal) =>
        client.POST('/v2/baselines', {
          params: {
            query: {
              area_id: areaIdValue,
            },
          },
          body: {
            has_intercept: hasInterceptValue,
            has_interactions: hasInteractionsValue,
            aggregation_level: aggregationLevelValue as 'day' | 'week' | 'month',
            variables: variablesValue,
          },
          signal: signal,
        }),
      onSuccess: (data) => {
        baseline.value = data;
      },
    });
  };

  const getBaselineData = async (baselineId: number) => {
    await cancelRequestIfDoubled({
      requestId: 'get-baseline-data',
      execute: async (signal) =>
        client.GET('/v2/baselines/{baseline_id}/data', {
          params: {
            path: {
              baseline_id: baselineId,
            },
          },
          signal: signal,
        }),
      onLoadingStart: () => {
        baselineDataLoading.value = true;
      },
      onLoadingEnd: () => {
        baselineDataLoading.value = false;
      },
      onSuccess: (data) => {
        baselineData.value = data;
        baselineDataLoading.value = false;
      },
      onError: (error) => {
        baselineData.value = undefined;
        baselineDataError.value = error.error_code ?? undefined;
        baselineDataLoading.value = false;
      },
    });
  };

  const trainBaseline = async (options: { baselineId: number; startDate: string; endDate: string }) => {
    await cancelRequestIfDoubled({
      requestId: 'train-baseline',
      execute: async (signal) =>
        client.POST('/v2/baselines/{baseline_id}/train', {
          params: {
            path: { baseline_id: options.baselineId },
            query: {
              training_start: options.startDate,
              training_end: options.endDate,
            },
          },
          signal: signal,
        }),
    });
  };

  const getBaselinePredictionWithReturn = async (options: { baselineId: number; startDate: string; endDate: string }) => {
    const response = await client.GET('/v2/baselines/{baseline_id}/predict', {
      params: {
        path: { baseline_id: options.baselineId },
        query: {
          start: options.startDate,
          end: options.endDate,
        },
      },
    });
    return response.data;
  };

  const getBaselinePrediction = async (options: { baselineId: number; startDate: string; endDate: string }) => {
    await cancelRequestIfDoubled({
      requestId: 'predict-baseline',
      execute: async (signal) =>
        client.GET('/v2/baselines/{baseline_id}/predict', {
          params: {
            path: { baseline_id: options.baselineId },
            query: {
              start: options.startDate,
              end: options.endDate,
            },
          },
          signal: signal,
        }),
      onLoadingStart: () => {
        baselinePredictionLoading.value = true;
      },
      onLoadingEnd: () => {
        baselinePredictionLoading.value = false;
      },
      onSuccess: (data) => {
        baselinePrediction.value = data;
      },
      onError: (error) => {
        baselinePrediction.value = undefined;
        baselinePredictionError.value = error.error_code ?? undefined;
      },
    });
  };

  return {
    createBaseline,
    getBaselineData,
    trainBaseline,
    getBaselines,
    getBaseline,
    getBaselinePrediction,
    getBaselinePredictionWithReturn,
  };
};
